import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import {
  Plus, Truck as TruckIcon, Package, Barcode, Warning, Check, ClipboardText, FileText,
  Eye, X, CaretRight, ListChecks, Lock, Prohibit, Pause, Trash, DownloadSimple,
  Camera, ImageSquare, Printer, Lightning, Gauge, CalendarCheck, ShieldCheck,
} from '@phosphor-icons/react';
import { updateDispatch, transitionDispatch, cancelDispatch, holdDispatch, dispatchManifestPdfUrl, markDispatchDelivered, markDispatchReturned } from '../../../lib/api';
import { SumItem, SummaryBar, STATUS_META, NEXT_STATUS } from '../_shared';

export const DetailsTab = ({ detail, isManager, onSaved, onOpenDock, onOpenShipping, onOpenHandoff }) => {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(detail);
  useEffect(() => { setForm(detail); }, [detail]);
  const editable = detail.status !== 'dispatched' && detail.status !== 'cancelled';

  const save = async () => {
    const patch = {
      customer_name: form.customer_name, carrier: form.carrier, transport_company: form.transport_company,
      container_number: form.container_number, trailer_rego: form.trailer_rego,
      driver_name: form.driver_name, driver_phone: form.driver_phone, driver_license: form.driver_license,
      planned_dispatch_at: form.planned_dispatch_at || null, destination: form.destination, notes: form.notes,
      reefer_temp_c: form.reefer_temp_c, dg_flag: !!form.dg_flag,
    };
    try { await updateDispatch(detail.id, patch); toast.success('Saved'); setEdit(false); await onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  return (
    <div className="mt-4 space-y-3" data-testid="details-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-widest text-foreground">Dispatch Details</h3>
        {isManager && editable && (
          <Button size="sm" variant="outline" className="rounded-none" onClick={() => setEdit(!edit)} data-testid="edit-details-btn">
            {edit ? 'Cancel' : 'Edit'}
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Field label="Customer" value={form?.customer_name} edit={edit} onChange={(v) => setForm({ ...form, customer_name: v })} />
        <Field label="Carrier" value={form?.carrier} edit={edit} onChange={(v) => setForm({ ...form, carrier: v })} />
        <Field label="Transport Co" value={form?.transport_company} edit={edit} onChange={(v) => setForm({ ...form, transport_company: v })} />
        <Field label="Container #" value={form?.container_number} edit={edit} onChange={(v) => setForm({ ...form, container_number: v })} mono />
        <Field label="Trailer Rego" value={form?.trailer_rego} edit={edit} onChange={(v) => setForm({ ...form, trailer_rego: v })} mono />
        <Field label="Driver name" value={form?.driver_name} edit={edit} onChange={(v) => setForm({ ...form, driver_name: v })} />
        <Field label="Driver phone" value={form?.driver_phone} edit={edit} onChange={(v) => setForm({ ...form, driver_phone: v })} mono />
        <Field label="Driver licence" value={form?.driver_license} edit={edit} onChange={(v) => setForm({ ...form, driver_license: v })} mono />
        <Field label="Destination" value={form?.destination} edit={edit} onChange={(v) => setForm({ ...form, destination: v })} />
        <Field
          label="Planned dispatch"
          value={form?.planned_dispatch_at ? new Date(form.planned_dispatch_at).toISOString().slice(0, 16) : ''}
          edit={edit}
          onChange={(v) => setForm({ ...form, planned_dispatch_at: v ? new Date(v).toISOString() : null })}
          type="datetime-local"
        />
        <Field label="Load type" value={form?.load_type} edit={false} />
        <Field label="Container type" value={form?.container_type} edit={false} mono />
        {form?.container_type?.includes('REEFER') && (
          <Field label="Reefer temp (°C)" value={form?.reefer_temp_c} edit={edit} onChange={(v) => setForm({ ...form, reefer_temp_c: v === '' ? null : Number(v) })} type="number" />
        )}
        <Field label="DG flag" value={form?.dg_flag ? 'Yes' : 'No'} edit={false} />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Notes</Label>
        {edit ? (
          <Textarea value={form?.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="rounded-none" />
        ) : (
          <p className="text-sm text-foreground">{form?.notes || <span className="text-muted-foreground">—</span>}</p>
        )}
      </div>
      {edit && (
        <Button onClick={save} data-testid="save-details-btn" className="bg-brand text-white rounded-none">Save changes</Button>
      )}

      {/* Phase 2a: Dock + Manual Shipping cards */}
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="border border-border p-3" data-testid="dock-card">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] uppercase tracking-widest font-bold text-foreground flex items-center gap-1">
              <CalendarCheck size={13} weight="bold" /> Dock appointment
            </p>
            {isManager && editable && (
              <Button size="sm" variant="outline" onClick={onOpenDock} data-testid="open-dock-btn" className="rounded-none h-6 text-[10px]">
                {detail.dock_booking ? 'Change' : 'Book'}
              </Button>
            )}
          </div>
          {detail.dock_booking ? (
            <div className="text-xs font-mono">
              <div className="font-bold text-brand">{detail.dock_booking.dock_code}</div>
              <div className="text-muted-foreground">
                {new Date(detail.dock_booking.start_at).toLocaleString()} — {new Date(detail.dock_booking.end_at).toLocaleTimeString()}
              </div>
              <div className="text-muted-foreground">{detail.dock_booking.duration_minutes}min slot</div>
            </div>
          ) : <p className="text-xs text-muted-foreground">No dock booked.</p>}
        </div>

        <div className="border border-border p-3" data-testid="shipping-card">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] uppercase tracking-widest font-bold text-foreground flex items-center gap-1">
              <TruckIcon size={13} weight="bold" /> Manual shipping
            </p>
            {isManager && detail.status !== 'cancelled' && (
              <Button size="sm" variant="outline" onClick={onOpenShipping} data-testid="open-shipping-btn" className="rounded-none h-6 text-[10px]">
                {detail.manual_shipping ? 'Edit' : 'Book'}
              </Button>
            )}
          </div>
          {detail.manual_shipping ? (
            <div className="text-xs">
              <div className="font-bold text-brand">{detail.manual_shipping.carrier_name}</div>
              <div className="font-mono text-muted-foreground">{detail.manual_shipping.tracking_number || '(no tracking #)'}</div>
              <div className="text-muted-foreground">
                {detail.manual_shipping.service_level || '—'}
                {detail.manual_shipping.shipping_cost != null && ` · ${detail.manual_shipping.shipping_currency || 'USD'} ${detail.manual_shipping.shipping_cost}`}
              </div>
              {detail.manual_shipping.estimated_delivery_at && (
                <div className="text-muted-foreground font-mono">
                  ETA {new Date(detail.manual_shipping.estimated_delivery_at).toLocaleString()}
                </div>
              )}
            </div>
          ) : <p className="text-xs text-muted-foreground">No manual shipping booked.</p>}
        </div>

        <div className="border border-border p-3" data-testid="carrier-handoff-card">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] uppercase tracking-widest font-bold text-foreground flex items-center gap-1">
              <ShieldCheck size={13} weight="bold" /> Carrier portal
            </p>
            {isManager && detail.status !== 'cancelled' && (
              <Button size="sm" variant="outline" onClick={onOpenHandoff}
                data-testid="open-handoff-btn"
                className="rounded-none h-6 text-[10px]">
                Generate
              </Button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Signed link a carrier can open with no login to view manifest + confirm pickup (signature + GPS + photo).
          </p>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, edit, onChange, type = 'text', mono = false }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</Label>
    {edit && onChange ? (
      <Input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="rounded-none" />
    ) : (
      <p className={`text-sm text-foreground ${mono ? 'font-mono' : ''}`}>{value || <span className="text-muted-foreground">—</span>}</p>
    )}
  </div>
);

